import { APIGet, APIPost, APIPut, APIDelete, IAPIResponse, AdminType, APIUrl, StatusCode } from "api/protocols";
import { IClient } from "api/rpc/2022-09/customer/client";
import { CancelToken } from "axios";
import { IPostAccountTransactionResponse } from "../facilityAdmin/customer/customer";
import { IKind } from "redux/reducers/models/transaction";

const apiVersion = "2024-04";
const adminType = "customer";
const baseUrl = "/" + apiVersion + "/" + adminType + "/customer";

interface IAddFriend {
  /**Add a single user */
  friend_user_id?: number;
  /**Add many users */
  friend_user_ids?: Array<number>;
}

interface IPutCustomer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birth_date?: string | null;
}

interface IPutCustomerPreferences {
  client_id: number;
  marketing_email?: boolean;
  booking_confirmation_email?: boolean;
  booking_reminder_email?: boolean;
  booking_update_email?: boolean;
  booking_cancellation_email?: boolean;
  display_name_on_tee_sheet?: boolean;
}

interface ISearchCustomer {
  client_id?: number;
  search?: string;
  member_search?: string;
  facility_id?: number;
}

export interface IPreferences {
  id: number;
  customer_id: number;
  client_id: number;
  client?: IClient;
  marketing_email: boolean;
  booking_confirmation_email: boolean;
  booking_reminder_email: boolean;
  booking_update_email: boolean;
  booking_cancellation_email: boolean;
  display_name_on_tee_sheet: boolean;
}

export interface ICustomerFriend {
  email: string;
  first_name: string;
  full_name: string;
  id: number;
  initials: string;
  last_name: string;
  phone: string;
  customer_type: Array<{ title: string }>;
}

export interface ICustomerCreditCard {
  brand: "visa" | "mastercard" | "discover" | "amex" | "jcb" | "diners";
  client_id: number;
  exp_month: number;
  exp_year: number;
  expiry: string;
  id: number;
  last4: string;
  stripe_payment_method_id: string;
  type: string;
  status?: "valid" | "expired";
}

export async function PutCustomer(params: IPutCustomer, useGlobalLoader: boolean) {
  return await APIPut(baseUrl, params, useGlobalLoader);
}

export async function UploadCustomerProfileImage(params: FormData, useGlobalLoader: boolean) {
  return await APIPost(baseUrl + "/profile-image", params, useGlobalLoader);
}

export async function DeleteCustomerProfileImage(useGlobalLoader: boolean) {
  return await APIDelete(baseUrl + "/profile-image", null, useGlobalLoader);
}

export async function GetCustomerPreferences(
  params: { client_id: number },
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
): Promise<IAPIResponse<Array<IPreferences>>> {
  return APIGet(baseUrl + "/preference", params, useGlobalLoader, cancelToken);
}

export async function PostCustomerPreferences(params: { client_id: number }, useGlobalLoader: boolean) {
  return APIPost(baseUrl + "/preference", params, useGlobalLoader);
}

export async function PutCustomerPreferences(params: IPutCustomerPreferences, useGlobalLoader: boolean) {
  return APIPut(baseUrl + "/preference", params, useGlobalLoader);
}

//PUT Add Friend
export async function PostFriends(params: IAddFriend, useGlobalLoader?: boolean) {
  return await APIPost(baseUrl + "/friend", params, useGlobalLoader);
}

export async function GetCustomerFriends(
  params: { search?: string; facility_id?: number },
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
): Promise<IAPIResponse<Array<ICustomerFriend>>> {
  return await APIGet(baseUrl + "/friend", params, useGlobalLoader, cancelToken);
}

export async function DeleteFriend(params: { friend_user_id: number }, useGlobalLoader?: boolean) {
  return await APIDelete(baseUrl + "/friend", params, useGlobalLoader);
}

export async function SearchCustomer(params: ISearchCustomer, useGlobalLoader: boolean, cancelToken?: CancelToken) {
  return await APIGet(baseUrl + "/search", params, useGlobalLoader, cancelToken);
}

export async function GetCustomerPaymentMethods(
  params: {
    client_id?: number;
    id?: number;
    type?: Array<"card" | "gift_card" | "rain_check">;
    include_expired?: boolean;
  },
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
) {
  return await APIGet(baseUrl + "/payment-method", params, useGlobalLoader, cancelToken);
}

//HOUSE ACCOUNTS

interface ICustomerRes {
  status: StatusCode;
  message?: string;
  data: any;
}

export async function GetCustomerHouseAccounts(
  params: { customer_id: number; closed?: boolean },
  useGlobalLoader?: boolean,
  token?: CancelToken,
): Promise<ICustomerRes> {
  return await APIGet(APIUrl(AdminType.Customer, "/account"), params, useGlobalLoader, token);
}

interface IGetCustomerStatements {
  id?: number;
  account_id?: number;
  limit?: number;
  offset?: number;
  filters?: Array<Record<string, any>>;
}

export async function GetCustomerStatements(
  params: IGetCustomerStatements,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.Customer, "/account/statement"), params, useGlobalLoader, token);
}

interface IDownloadCustomerAccountStatements {
  account_statement_id: number;
  account_id: number;
  start_date: Date;
  end_date: Date;
}

export async function DownloadCustomerAccountStatements(
  params: Partial<IDownloadCustomerAccountStatements>,
  useGlobalLoader: boolean,
) {
  return await APIGet(APIUrl(AdminType.Customer, "/account/statement/download"), params, useGlobalLoader);
}

//GET AccountTransactions
//Get a list of account transactions
export type TAccountTransactionsGetParams = {
  account_id: number;
  limit?: number;
  offset?: number;
  filters?: Array<Record<string, any>>;
};

export async function GetAccountTransactions(
  params: TAccountTransactionsGetParams,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.Customer, "/account/transaction"), params, useGlobalLoader, token);
}

interface IGetAccountScheduledPayments {
  account_id: number;
  limit?: number;
  offset?: number;
  filters?: Array<Record<string, any>>;
}

//GET Account Scheduled Payments
export async function GetAccountScheduledPayments(
  params: IGetAccountScheduledPayments,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.Customer, "/account/scheduled-payment"), params, useGlobalLoader, token);
}

//CREDIT BOOK

interface IGetCustomerCredit {
  customer_id?: number;
  facility_id?: number;
}

interface ICustomerCreditRes {
  status: StatusCode;
  message?: string;
  data: any;
}

interface IGetCreditTransactionsParams {
  credit_book_id: number;
  customer_id?: number;
  id?: number;
  facility_id?: number;
  limit?: number;
  offset?: number;
  filters?: Array<Record<string, any>>;
}

interface IGetCreditTransactions extends IAPIResponse {
  data: Array<ICreditTransactionRes>;
}

export interface ICreditTransactionRes {
  id: number;
  credit_book_id: number;
  facility_id: number;
  client_id: number;
  customer_id: number;
  amount: number;
  currency: string;
  kind: string;
  parent_id: number;
  order_id: number;
  order_number: string;
  order_name: string;
  processed_at_local: string;
  note: string;
}

export async function GetCustomerCredit(
  params: IGetCustomerCredit,
  useGlobalLoader?: boolean,
  cancelToken?: CancelToken,
): Promise<ICustomerCreditRes> {
  return await APIGet(APIUrl(AdminType.Customer, "/credit-book"), params, useGlobalLoader, cancelToken);
}

export async function GetCreditBookTransactions(
  params: IGetCreditTransactionsParams,
  useGlobalLoader: boolean,
): Promise<IGetCreditTransactions> {
  return APIGet(APIUrl(AdminType.Customer, "/credit-book/transaction"), params, useGlobalLoader);
}

// ITEM SALES

interface IGetOrder {
  id?: number;
  search?: string;
  number?: string;
  customer_id?: number;
  extended?: boolean;
  refund?: boolean;
  limit?: number;
  offset?: number;
  filters?: Array<Record<string, any>>;
}

// GET Order
// Retrieve a list of orders for a facility
// extended: boolean
export async function GetOrder(params: IGetOrder, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet(APIUrl(AdminType.Customer, "/order"), params, useGlobalLoader, token);
}

interface IPostCustomerAccountTransaction {
  kind: IKind;
  amount: number;
  account_id: number;
  payment_option_id: number;
  facility_id?: number;
  gift_card_code?: string;
  gift_card_pin?: string;
  note?: string;
}

export async function PostAccountTransaction(
  params: IPostCustomerAccountTransaction,
  useGlobalLoader: boolean,
): Promise<IPostAccountTransactionResponse> {
  return await APIPost(APIUrl(AdminType.Customer, "/account/transaction"), params, useGlobalLoader);
}

interface ICustomerAccountTransactionCapture {
  transaction_id: number;
  payment_intent_id: string;
}

export async function PutAccountTransactionCapture(
  params: ICustomerAccountTransactionCapture,
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.Customer, "/account/transaction/capture"), params, useGlobalLoader);
}

interface IGetCustomerAddressLines {
  customer_id: number;
}

export async function GetCustomerAddressLines(params: IGetCustomerAddressLines, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.Customer, "/customer/address-lines"), params, useGlobalLoader);
}

export async function GetRWGToken(useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.Customer, "/customer/rwg-token"), null, useGlobalLoader);
}

export async function PostRWGToken(
  params: { rwg_token: string; facility_id: number; customer_id: number },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.Customer, "/customer/rwg-token"), params, useGlobalLoader);
}

export async function PostRWGTokenConversion(
  params: { rwg_token_id: number; merchant_changed: number; conversion_partner_id: string },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.Customer, "/customer/rwg-token/conversion"), params, useGlobalLoader);
}
